import React from "react";
import styled from "styled-components";
import { Building2, ChevronsUpDown, Search, Check, Plus } from "lucide-react";
import { useProjectContext } from "../../context/useProjectContext";
import { useSearchParams } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../../shadcn/popover";
import { cn } from "../../shadcn/lib/utils";

const SEARCH_THRESHOLD = 5;

const ProjectIcon = ({ selected }) => (
  <span
    className={cn(
      "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
      selected ? "bg-white/15 text-white" : "bg-brand-50 text-brand-700"
    )}
  >
    <Building2 size={16} />
  </span>
);

const ProjectsMenu = ({ setCreateNewProjectModalOpen }) => {
  const { selectedProject, setSelectedProject, projects } = useProjectContext();
  const [, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const projectList = projects || [];
  const showSearch = projectList.length > SEARCH_THRESHOLD;

  const filteredProjects =
    showSearch && searchTerm
      ? projectList.filter((project) =>
          (project.name || "").toLowerCase().includes(searchTerm.toLowerCase())
        )
      : projectList;

  const handleSelect = (project) => {
    setSearchParams((params) => {
      params.set("project_id", String(project.id));
      return params;
    });
    setSelectedProject(project);
    setIsOpen(false);
  };

  React.useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  return (
    <Wrapper>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-left transition-colors hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600/30"
          >
            <ProjectIcon />
            <span className={cn("min-w-0 flex-1 truncate text-sm font-semibold leading-tight", selectedProject ? "text-gray-900" : "text-gray-500")}>
              {selectedProject ? selectedProject.name : "Select Project"}
            </span>
            <ChevronsUpDown size={15} className="shrink-0 text-gray-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          {showSearch && (
            <div className="p-2">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5">
                <Search size={15} className="shrink-0 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full border-none bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                />
              </div>
            </div>
          )}

          <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Subscriptions
          </div>

          <ProjectList className="p-1.5 pt-0.5">
            {filteredProjects.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-gray-500">No projects found</div>
            ) : (
              filteredProjects.map((project) => {
                const isSelected = selectedProject && String(selectedProject.id) === String(project.id);
                return (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => handleSelect(project)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm transition-colors",
                      isSelected ? "bg-[#8580fd] font-semibold text-white" : "text-gray-900 hover:bg-gray-100"
                    )}
                  >
                    <ProjectIcon selected={isSelected} />
                    <span className="flex-1 truncate">{project.name}</span>
                    {isSelected && <Check size={16} className="shrink-0 text-white" />}
                  </button>
                );
              })
            )}
          </ProjectList>

          <div className="border-t border-gray-200 p-2">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setCreateNewProjectModalOpen && setCreateNewProjectModalOpen(true);
              }}
              className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <Plus size={16} />
              Add New Subscription
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </Wrapper>
  );
};

export default ProjectsMenu;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 40px;
  margin-bottom: 8px;
`;

const ProjectList = styled.div`
  max-height: 288px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;
